import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { promotionService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { Promotion, PromotionCompany, Coupon } from '../types/promotion';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import PromotionList from '../components/promotions/PromotionList';
import PromotionForm from '../components/promotions/PromotionForm';
import CompanyList from '../components/promotions/CompanyList';
import CompanyForm from '../components/promotions/CompanyForm';
import CouponList from '../components/promotions/CouponList';
import CouponForm from '../components/promotions/CouponForm';
import Modal from '../components/ui/Modal';

type ModalType = 'create' | 'edit' | 'companies' | 'addCompany' | 'coupons' | 'addCoupon' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function Promotions() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<PromotionCompany | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: promotions } = useQuery({
    queryKey: ['promotions'],
    queryFn: () => promotionService.getPromotions(),
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => promotionService.getCompanies(),
    enabled: modal.type === 'addCompany',
  });

  const { data: participants } = useQuery({
    queryKey: ['participants'],
    queryFn: () => promotionService.getParticipants(),
  });

  const { data: promotionCompanies } = useQuery({
    queryKey: ['promotion-companies', editingPromotion?.id],
    queryFn: () => promotionService.getPromotionCompanies(editingPromotion!.id),
    enabled: !!editingPromotion && modal.type === 'companies',
  });

  const { data: coupons } = useQuery({
    queryKey: ['coupons', selectedCompany?.id],
    queryFn: () => promotionService.getCoupons(selectedCompany!.id),
    enabled: !!selectedCompany && modal.type === 'coupons',
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Promotion>) => promotionService.createPromotion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      messageHandler.success('Campanha criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Promotion) => promotionService.updatePromotion(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      messageHandler.success('Campanha atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => promotionService.deletePromotion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      messageHandler.success('Campanha excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const addCompanyMutation = useMutation({
    mutationFn: (data: Partial<PromotionCompany>) => promotionService.addCompanyToPromotion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-companies'] });
      messageHandler.success('Empresa adicionada com sucesso!');
      setModal({ type: 'companies' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: (data: PromotionCompany) => promotionService.updatePromotionCompany(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-companies'] });
      messageHandler.success('Empresa atualizada com sucesso!');
      setModal({ type: 'companies' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: (id: number) => promotionService.removeCompanyFromPromotion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-companies'] });
      messageHandler.success('Empresa removida com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const addCouponMutation = useMutation({
    mutationFn: (data: Partial<Coupon>) => promotionService.addCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      messageHandler.success('Cupom adicionado com sucesso!');
      setModal({ type: 'coupons' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteCouponMutation = useMutation({
    mutationFn: (id: number) => promotionService.deleteCoupon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      messageHandler.success('Cupom removido com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      name: formData.get('name') as string,
      start_date: formData.get('start_date') as string,
      end_date: formData.get('end_date') as string,
    };

    if (editingPromotion) {
      updateMutation.mutate({ ...editingPromotion, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleCompanySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      promotion_id: editingPromotion?.id,
      company_id: Number(formData.get('company_id')),
      coupon_quantity: Number(formData.get('coupon_quantity')),
    };

    if (selectedCompany) {
      updateCompanyMutation.mutate({ ...selectedCompany, ...data } as PromotionCompany);
    } else {
      addCompanyMutation.mutate(data);
    }
  };

  const handleCouponSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      promotion_company_id: selectedCompany?.id,
      participant_id: Number(formData.get('participant_id')),
      quantity: Number(formData.get('quantity')),
    };

    addCouponMutation.mutate(data);
  };

  const handleDelete = (promotion: Promotion) => {
    messageHandler.confirm(
      'Excluir Campanha',
      'Tem certeza que deseja excluir esta campanha?',
      () => deleteMutation.mutate(promotion.id)
    );
  };

  const handleDeleteCompany = (company: PromotionCompany) => {
    messageHandler.confirm(
      'Remover Empresa',
      'Tem certeza que deseja remover esta empresa da campanha?',
      () => deleteCompanyMutation.mutate(company.id)
    );
  };

  const handleDeleteCoupon = (coupon: Coupon) => {
    messageHandler.confirm('Excluir Cupom', 'Tem certeza que deseja excluir este cupom?', () =>
      deleteCouponMutation.mutate(coupon.id)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingPromotion(null);
    setSelectedCompany(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Campanhas"
          description="Gerencie suas campanhas promocionais."
          actions={
            <Button
              onClick={() => {
                setEditingPromotion(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Campanha
            </Button>
          }
        />

        {promotions?.length > 0 ? (
          <PromotionList
            promotions={promotions}
            onEdit={promotion => {
              setEditingPromotion(promotion);
              setModal({ type: 'edit' });
            }}
            onDelete={handleDelete}
            onManageCompanies={promotion => {
              setEditingPromotion(promotion);
              setModal({ type: 'companies' });
            }}
          />
        ) : (
          <EmptyState
            icon={<Plus className="h-12 w-12" />}
            title="Nenhuma campanha cadastrada"
            description="Crie sua primeira campanha promocional para começar"
            action={{
              label: 'Criar Campanha',
              onClick: () => {
                setEditingPromotion(null);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingPromotion ? 'Editar Campanha' : 'Nova Campanha'}
        >
          <PromotionForm
            promotion={editingPromotion}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>

        {/* Companies Modal */}
        <Modal
          isOpen={modal.type === 'companies'}
          onClose={closeModal}
          title={`Empresas Participantes - ${editingPromotion?.name}`}
          size="lg"
        >
          <div className="mb-4">
            <Button
              onClick={() => {
                setSelectedCompany(null);
                setModal({ type: 'addCompany' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Empresa
            </Button>
          </div>

          <CompanyList
            companies={promotionCompanies || []}
            onEdit={company => {
              setSelectedCompany(company);
              setModal({ type: 'addCompany' });
            }}
            onDelete={handleDeleteCompany}
            onManageCoupons={company => {
              setSelectedCompany(company);
              setModal({ type: 'coupons' });
            }}
          />
        </Modal>

        {/* Add/Edit Company Modal */}
        <Modal
          isOpen={modal.type === 'addCompany'}
          onClose={() => setModal({ type: 'companies' })}
          title={selectedCompany ? 'Editar Empresa Participante' : 'Adicionar Empresa'}
        >
          <CompanyForm
            company={selectedCompany}
            companies={Array.isArray(companies) ? companies : []}
            onSubmit={handleCompanySubmit}
            isLoading={addCompanyMutation.isPending || updateCompanyMutation.isPending}
            onClose={() => setModal({ type: 'companies' })}
          />
        </Modal>

        {/* Coupons Modal */}
        <Modal
          isOpen={modal.type === 'coupons'}
          onClose={() => setModal({ type: 'companies' })}
          title={`Cupons - ${selectedCompany?.company_name}`}
          size="lg"
        >
          <div className="mb-4">
            <Button onClick={() => setModal({ type: 'addCoupon' })}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Cupom
            </Button>
          </div>

          <CouponList coupons={coupons || []} onDelete={handleDeleteCoupon} />
        </Modal>

        {/* Add Coupon Modal */}
        <Modal
          isOpen={modal.type === 'addCoupon'}
          onClose={() => setModal({ type: 'coupons' })}
          title="Adicionar Cupom"
        >
          <CouponForm
            participants={participants || []}
            onSubmit={handleCouponSubmit}
            isLoading={addCouponMutation.isPending}
            onClose={() => setModal({ type: 'coupons' })}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Promotions;
